import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { MeshBackground } from "@/components/marketing/MeshBackground";
import "@/styles/marketing.css";
import {
  DEFAULT_TRANSPARENCY,
  applyTransparencyToDocument,
  fetchTransparencyConfig,
  getPreviewTransparency,
  saveTransparencyConfig,
  setPreviewTransparency,
  type TransparencyConfig,
} from "@/lib/transparencyConfig";

type Field = {
  key: keyof TransparencyConfig;
  label: string;
  help: string;
  min: number;
  max: number;
  step: number;
  unit: "%" | "px";
};

const FIELDS: Field[] = [
  { key: "card_opacity", label: "Standard card opacity", help: "Pricing tiers, landing cards, FAQs.", min: 0, max: 100, step: 1, unit: "%" },
  { key: "card_blur", label: "Standard card blur", help: "Backdrop blur on standard cards.", min: 0, max: 40, step: 1, unit: "px" },
  { key: "card_featured_opacity", label: "Featured card opacity", help: "Pro tier / 'Most popular' card.", min: 0, max: 100, step: 1, unit: "%" },
  { key: "card_featured_blur", label: "Featured card blur", help: "Backdrop blur on the featured card.", min: 0, max: 40, step: 1, unit: "px" },
  { key: "nav_opacity", label: "Header pill opacity", help: "Floating marketing nav.", min: 0, max: 100, step: 1, unit: "%" },
  { key: "footer_opacity", label: "Footer pill opacity", help: "Floating marketing footer.", min: 0, max: 100, step: 1, unit: "%" },
  { key: "menu_opacity", label: "Mega-menu opacity", help: "Features dropdown panel background.", min: 0, max: 100, step: 1, unit: "%" },
  { key: "menu_blur", label: "Mega-menu blur", help: "Backdrop blur on the Features dropdown panel.", min: 0, max: 40, step: 1, unit: "px" },
  { key: "menu_item_hover_opacity", label: "Mega-menu item hover opacity", help: "Background opacity on hover for each item card inside the Features panel.", min: 0, max: 100, step: 1, unit: "%" },
  { key: "menu_item_hover_blur", label: "Mega-menu item hover blur", help: "Backdrop blur on hover for each item card inside the Features panel.", min: 0, max: 40, step: 1, unit: "px" },
];

export const TransparencyLevelsTab = () => {
  const [config, setConfig] = useState<TransparencyConfig>(DEFAULT_TRANSPARENCY);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [livePreview, setLivePreview] = useState<boolean>(() => getPreviewTransparency() !== null);

  useEffect(() => {
    void fetchTransparencyConfig().then((c) => {
      setConfig(c);
      setLoaded(true);
    });
  }, []);

  // Always apply the current editor values to the local document so the in-tab
  // preview is live. If "Preview on marketing pages" is on, also persist to
  // localStorage so other tabs see it.
  useEffect(() => {
    if (!loaded) return;
    applyTransparencyToDocument(config);
    if (livePreview) setPreviewTransparency(config);
  }, [config, loaded, livePreview]);

  const update = (key: keyof TransparencyConfig, value: number) =>
    setConfig((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveTransparencyConfig(config);
      toast.success("Transparency settings saved");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(DEFAULT_TRANSPARENCY);
    toast.message("Reset to defaults (not yet saved)");
  };

  const togglePreview = (on: boolean) => {
    setLivePreview(on);
    if (on) setPreviewTransparency(config);
    else setPreviewTransparency(null);
  };

  return (
    <div className="h-[calc(100vh-220px)]">
    <ResizablePanelGroup direction="horizontal" className="h-full rounded-lg border">
      {/* Left — header / actions */}
      <ResizablePanel defaultSize={22} minSize={16}>
        <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
          <div>
            <h2 className="font-serif text-lg font-semibold">Transparency levels</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Tune opacity and blur for marketing surfaces. Defaults match the
              approved "Frosted with bold featured" direction.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="tp-live" className="text-sm">Preview on marketing pages</Label>
              <p className="text-xs text-muted-foreground">Local override only.</p>
            </div>
            <Switch id="tp-live" checked={livePreview} onCheckedChange={togglePreview} />
          </div>
          <div className="flex flex-col gap-2">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save settings"}
            </Button>
            <Button variant="outline" onClick={handleReset}>Reset to defaults</Button>
          </div>
        </div>
      </ResizablePanel>

      <ResizableHandle withHandle />

      {/* Center — sliders */}
      <ResizablePanel defaultSize={48} minSize={30}>
        <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
          {FIELDS.map((f) => {
            const value = config[f.key];
            return (
              <Card key={f.key}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    {f.label}
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {value}{f.unit}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Slider
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={[value]}
                    onValueChange={([v]) => update(f.key, v)}
                  />
                  <p className="text-xs text-muted-foreground">{f.help}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </ResizablePanel>

      <ResizableHandle withHandle />

      {/* Right — sticky preview */}
      <ResizablePanel defaultSize={30} minSize={22}>
        <div className="flex h-full flex-col gap-3 p-4">
          <h3 className="text-sm font-medium">Preview</h3>
          <div
            data-marketing="true"
            className="relative flex-1 overflow-hidden rounded-lg border bg-background"
          >
            <MeshBackground contained />
            <div className="relative z-10 flex h-full flex-col gap-3 p-4">
              <div className="mkt-glass rounded-full px-4 py-2 text-xs text-foreground">
                Header pill preview
              </div>
              <div className="bg-card rounded-xl border p-4 shadow-soft">
                <div className="font-serif text-base font-semibold">Standard card</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Uses standard opacity + blur tokens.
                </p>
              </div>
              <div className="mkt-glass-card-featured bg-card rounded-xl border p-4 shadow-soft">
                <div className="font-serif text-base font-semibold">Featured card</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Pro tier / "Most popular".
                </p>
              </div>
              <div className="mkt-menu-panel rounded-lg p-3">
                <div className="text-xs font-bold text-primary">Mega-menu panel</div>
                <div className="mt-2 space-y-1 text-xs text-foreground/80">
                  <div className="mkt-menu-item rounded-md p-1.5 ring-1 ring-transparent transition-colors hover:ring-border cursor-default">· Excerpts <span className="text-[10px] text-muted-foreground">(hover me)</span></div>
                  <div className="mkt-menu-item rounded-md p-1.5 ring-1 ring-transparent transition-colors hover:ring-border cursor-default">· Library</div>
                  <div className="mkt-menu-item rounded-md p-1.5 ring-1 ring-transparent transition-colors hover:ring-border cursor-default">· Composer</div>
                </div>
              </div>
              <div className="mt-auto mkt-footer-glass rounded-2xl px-4 py-3 text-xs text-foreground">
                Footer pill preview
              </div>
            </div>
          </div>
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
    </div>
  );
};

